import React from 'react';
import * as PropTypes from 'prop-types';
import {
  Col, Empty, Icon, Row, Tag, Tree, Typography,
} from 'antd';
import { FormattedMessage } from 'react-intl';
import { inspectionShape } from '../utility/inspection-util';

const { TreeNode } = Tree;
const { Text } = Typography;
const { Title } = Typography;

/**
 * Show the answer of an inspection checklist question
 */
const ViaChecklistAnswer = ({
  isNotApplicable, integerAnswer, booleanAnswer,
}) => {
  let showNA = false;
  let showPass = false;
  let showFail = false;
  let showNumber = false;

  if (isNotApplicable) {
    showNA = true;
  } else if (integerAnswer != null && integerAnswer >= 0) {
    showNumber = true;
  } else {
    showPass = booleanAnswer;
    showFail = !booleanAnswer && !showNA && !showNumber;
  }

  return (
    <Row type="flex" align="middle">
      <Tag className="via-checklist-tag" color="#3399ff" style={{ textAlign: 'center', width: '60px', display: showNA ? '' : 'none' }}>
        <FormattedMessage id="NA" />
      </Tag>
      <Tag className="via-checklist-tag" color="#00b33c" style={{ textAlign: 'center', width: '60px', display: showPass ? '' : 'none' }}>
        <FormattedMessage id="pass" />
      </Tag>
      <Tag className="via-checklist-tag" color="#e68a00" style={{ textAlign: 'center', width: '60px', display: showFail ? '' : 'none' }}>
        <FormattedMessage id="fail" />
      </Tag>
      <Tag className="via-checklist-tag" color="#5c5c8a" style={{ textAlign: 'center', width: '60px', display: showNumber ? '' : 'none' }}>
        {integerAnswer}
      </Tag>
    </Row>
  );
};
ViaChecklistAnswer.propTypes = {
  isNotApplicable: PropTypes.bool,
  integerAnswer: PropTypes.number,
  booleanAnswer: PropTypes.bool,
};
ViaChecklistAnswer.defaultProps = {
  isNotApplicable: false,
  integerAnswer: null,
  booleanAnswer: null,
};

/**
 * Used to style and add detail to the read only tree nodes for an inspection checklist
 */
const ViaInspectionTreeTitle = ({
  title, isQuestion, icon, bold, booleanAnswer, integerAnswer, isNotApplicable,
}) => (
  <Row
    type="flex"
    align="middle"
    justify="space-between"
    style={{ marginRigh: '2em' }}
    className={isQuestion ? 'via-checklist-node-item' : 'via-checklist-node-category'}
  >
    <Col span={21}>
      <Row type="flex" align="middle">
        <Col style={{ width: icon ? '25px' : '0' }}>
          <Row type="flex" align="middle">
            <Icon style={{ display: icon ? '' : 'none' }} className="via-checklist-icon" type={icon || 'right'} />
          </Row>
        </Col>
        <Col style={{ width: icon ? 'calc(100% - 25px)' : '100%' }}>
          <Text style={{ fontWeight: bold ? 'bold' : '', whiteSpace: 'pre-wrap' }}>{title}</Text>
        </Col>
      </Row>
    </Col>
    <Col span={3}>
      <Row type="flex" align="middle" justify="end">
        {
          isQuestion
            ? <ViaChecklistAnswer booleanAnswer={booleanAnswer} integerAnswer={integerAnswer} isNotApplicable={isNotApplicable} />
            : <></>
        }
      </Row>
    </Col>
  </Row>
);

ViaInspectionTreeTitle.propTypes = {
  bold: PropTypes.bool,
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  isQuestion: PropTypes.bool.isRequired,
  isNotApplicable: PropTypes.bool,
  integerAnswer: PropTypes.number,
  booleanAnswer: PropTypes.bool,
};

ViaInspectionTreeTitle.defaultProps = {
  bold: false,
  icon: '',
  isNotApplicable: false,
  integerAnswer: null,
  booleanAnswer: null,
};

/**
 * Read-only view of inspection checklist
 */
const ViaInspectionChecklistView = ({ checklistData }) => {
  if (checklistData.categories.length < 1) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<FormattedMessage id="invalidChecklist" />}
      />
    );
  }

  return (
    <Row>
      <Tree
        className="via-checklist"
        showIcon
        defaultExpandAll
        defaultSelectedKeys={[]}
        selectable={false}
        switcherIcon={<Icon className="via-config-expand-switcher" type="caret-down" />}
      >
        {
          checklistData.categories.sort((cat1, cat2) => cat1.rankInCheckList - cat2.rankInCheckList).map((category) => {
            const catKey = `${category.categoryCode}`;
            return (
              <TreeNode
                title={(<ViaInspectionTreeTitle title={category.categoryDescription} isQuestion={false} />)}
                key={catKey}
              >
                {
                  // eslint-disable-next-line max-len
                [...category.items].sort((itm1, itm2) => (itm1.rankInCategory - itm2.rankInCategory)).map(item => (
                  <TreeNode
                    key={`${category.categoryCode}-${item.itemCode}`}
                    title={(
                      <ViaInspectionTreeTitle
                        title={item.itemDescription}
                        icon="question-circle"
                        isQuestion
                        isNotApplicable={item.notApplicable}
                        integerAnswer={item.integerAnswer}
                        booleanAnswer={item.booleanAnswer}
                      />
              )}
                  />
                ))
              }
              </TreeNode>
            );
          })
        }
      </Tree>
    </Row>
  );
};

ViaInspectionChecklistView.propTypes = {
  checklistData: PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    categories: PropTypes.arrayOf(PropTypes.object),
  }),
};

ViaInspectionChecklistView.defaultProps = {
  checklistData: null,
};


/**
 * Main component to show checklists on inspection.
 */
export default function InspectionChecklist({ inspection, position }) {

  // Get correct checklist by position
  const checklists = inspection.checklists || [];
  let checklist = null;
  checklists.forEach(possibleCL => {
    if (possibleCL.checklistPosition === position) {
      checklist = possibleCL;
    }
  })

  // If the checklist is not defined against an inspection then there is no reason to display anything.
  if (!checklist || checklist.categories.length <= 0) return <></>;

  return (
    <div>
      <Row type="flex" align="middle" justify="space-between">
        <Title level={3}>
          {checklist.description && checklist.description !== 'Checklist'
              ? checklist.description
              : <FormattedMessage id="checklist" />
          }
        </Title>
      </Row>
      <div className="">
        <ViaInspectionChecklistView checklistData={checklist} />
      </div>
    </div>
  );
}

InspectionChecklist.propTypes = {
  inspection: inspectionShape.isRequired,
};
